/* The daily goal on a page: the goal from the settings that follow the account, the hands per
   day from the database, counted in this browser's time zone, and the pill in the top bar
   that says where today stands. Never waited for by a page, and silent when it fails: offline
   there is simply no pill. docs/specs/2026-09-20-daily-goal-design.md */

import { dayOf, readDays, readGoal, streaks, type Streaks } from "@preflop/api";
import type { Topbar } from "../ui/topbar";
import { supabase } from "./client";
import { loadSetting, saveSetting } from "./settings";

const DAILY_GOAL = "dailyGoal";

export const loadGoal = async (): Promise<number> =>
  readGoal(await loadSetting<unknown>(DAILY_GOAL, null));

/** Resolves to the goal as kept: brought in range, 0 for none. */
export async function saveGoal(goal: number): Promise<number> {
  const kept = readGoal(goal);
  await saveSetting(DAILY_GOAL, kept);
  return kept;
}

export interface DailyView extends Streaks {
  goal: number;
}

export interface Daily {
  view(): DailyView;
  /** One more hand answered today, counted here without asking the database. */
  add(): DailyView;
}

const browserTimeZone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
};

/** Where today stands. Null when the user has no goal: there is then nothing to show. */
export async function loadDaily(): Promise<Daily | null> {
  const goal = await loadGoal();
  if (!goal) return null;
  const { timeZone, hands } = await readDays(supabase(), browserTimeZone());
  // Told in the zone the days were counted in, which is UTC when the database knew no better.
  const today = (): string => dayOf(new Date(), timeZone);
  const view = (): DailyView => ({ goal, ...streaks(hands, goal, today()) });
  return {
    view,
    add() {
      hands.set(today(), (hands.get(today()) ?? 0) + 1);
      return view();
    },
  };
}

export interface DailyPill {
  /** A hand was just answered on this page. */
  add(): void;
  /** Ask the database again: what was played has been sent. */
  refresh(): void;
}

/** Put the pill in a page's top bar, once the figures are in. */
export function wireDaily(bar: Pick<Topbar, "setDaily">): DailyPill {
  let daily: Daily | null = null;
  const refresh = (): void =>
    void loadDaily()
      .then((loaded) => {
        daily = loaded;
        bar.setDaily(loaded?.view() ?? null);
      })
      .catch(() => undefined);
  refresh();
  return {
    add: () => daily && bar.setDaily(daily.add()),
    refresh,
  };
}
